import { Node } from "@tiptap/core";

let registrySequence = 0;

const RAW_TEXT_ELEMENTS = new Set([
  "script",
  "style",
  "textarea",
  "title",
  "xmp",
  "iframe",
  "noembed",
  "noframes",
  "noscript",
  "plaintext"
]);

const SAFE_PREVIEW_ELEMENTS = new Set([
  "table", "caption", "colgroup", "col", "thead", "tbody", "tfoot",
  "tr", "th", "td", "p", "br", "strong", "b", "em", "i", "u",
  "s", "del", "ins", "sub", "sup", "span", "small", "mark", "code",
  "ul", "ol", "li"
]);

const DROP_PREVIEW_CONTENT = new Set([
  "script", "style", "iframe", "object", "embed", "form", "input", "button",
  "textarea", "select", "option", "link", "meta", "base", "template", "svg", "math"
]);

const SAFE_PREVIEW_CLASSES = [
  /^table(?:-(?:striped|striped-columns|bordered|borderless|hover|sm|dark|active|responsive))?$/,
  /^table-(?:primary|secondary|success|danger|warning|info|light|dark)$/,
  /^align-(?:top|middle|bottom)$/,
  /^text-(?:start|center|end|wrap|nowrap|break)$/,
  /^fw-(?:bold|bolder|semibold|normal|light|lighter)$/
];

function readTag(source, start) {
  if (source.startsWith("<!--", start)) {
    const commentEnd = source.indexOf("-->", start + 4);
    return {
      end: commentEnd === -1 ? source.length : commentEnd + 3,
      special: true
    };
  }

  let quote = null;
  let cursor = start + 1;

  for (; cursor < source.length; cursor += 1) {
    const character = source[cursor];

    if (quote) {
      if (character === quote) quote = null;
      continue;
    }

    if (character === '"' || character === "'") {
      quote = character;
    } else if (character === "<") {
      // Recover from a stray '<' in text instead of swallowing the next real
      // element (which could be the table we need to protect).
      return { end: cursor, special: true };
    } else if (character === ">") {
      break;
    }
  }

  const end = cursor < source.length ? cursor + 1 : source.length;
  const body = source.slice(start + 1, cursor);
  const match = body.match(/^(\/?)([a-zA-Z][\w:-]*)(?=[\t\n\f\r ]|\/|$)/);

  if (!match) return { end, special: true };

  return {
    end,
    name: match[2].toLowerCase(),
    closing: match[1] === "/",
    selfClosing: /\/\s*$/.test(body)
  };
}

function nextTag(source, start) {
  const tagStart = source.indexOf("<", start);
  if (tagStart === -1) return null;
  return { start: tagStart, ...readTag(source, tagStart) };
}

function findRawTextEnd(source, token) {
  if (token.name === "plaintext") return source.length;

  let cursor = token.end;

  while (cursor < source.length) {
    const closingStart = source.indexOf("<", cursor);
    if (closingStart === -1) return source.length;

    const closingTag = readTag(source, closingStart);
    if (
      !closingTag.special &&
      closingTag.closing &&
      closingTag.name === token.name
    ) {
      return closingTag.end;
    }

    cursor = Math.max(closingTag.end, closingStart + 1);
  }

  return source.length;
}

// Template contents are inert HTML, not raw text, and templates can nest. Skip
// the complete outer template so table-looking examples inside it are not
// mistaken for live editor tables.
function findTemplateEnd(source, openingTemplate) {
  let depth = 1;
  let cursor = openingTemplate.end;

  while (cursor < source.length) {
    const token = nextTag(source, cursor);
    if (!token) return source.length;

    if (!token.special && !token.closing && RAW_TEXT_ELEMENTS.has(token.name)) {
      cursor = findRawTextEnd(source, token);
      continue;
    }

    cursor = token.end;
    if (token.special || token.name !== "template") continue;

    depth += token.closing ? -1 : 1;
    if (depth === 0) return token.end;
  }

  return source.length;
}

function findTableEnd(source, openingTable) {
  let depth = 1;
  let cursor = openingTable.end;

  while (cursor < source.length) {
    const token = nextTag(source, cursor);
    if (!token) return -1;

    if (
      !token.special &&
      !token.closing &&
      RAW_TEXT_ELEMENTS.has(token.name)
    ) {
      cursor = findRawTextEnd(source, token);
      continue;
    }

    if (!token.special && !token.closing && token.name === "template") {
      cursor = findTemplateEnd(source, token);
      continue;
    }

    cursor = token.end;
    if (token.special || token.name !== "table") continue;

    if (token.closing) {
      depth -= 1;
      if (depth === 0) return token.end;
    } else {
      // In text/html, a slash does not self-close a <table> element.
      depth += 1;
    }
  }

  return -1;
}

function replaceTables(source, replacement) {
  let cursor = 0;
  let output = "";

  while (cursor < source.length) {
    const token = nextTag(source, cursor);
    if (!token) {
      output += source.slice(cursor);
      break;
    }

    if (
      !token.special &&
      !token.closing &&
      RAW_TEXT_ELEMENTS.has(token.name)
    ) {
      const rawTextEnd = findRawTextEnd(source, token);
      output += source.slice(cursor, rawTextEnd);
      cursor = rawTextEnd;
      continue;
    }


    if (!token.special && !token.closing && token.name === "template") {
      const templateEnd = findTemplateEnd(source, token);
      output += source.slice(cursor, templateEnd);
      cursor = templateEnd;
      continue;
    }

    if (token.special || token.closing || token.name !== "table") {
      output += source.slice(cursor, token.end);
      cursor = token.end;
      continue;
    }

    const tableEnd = findTableEnd(source, token);
    if (tableEnd === -1) {
      output += source.slice(cursor);
      break;
    }

    output += source.slice(cursor, token.start);
    output += replacement(source.slice(token.start, tableEnd));
    cursor = tableEnd;
  }

  return output;
}

function boundedInteger(value, maximum) {
  if (!/^\d+$/.test(value)) return null;
  const number = Number(value);
  return number >= 0 && number <= maximum ? String(number) : null;
}

function safePreviewAttribute(elementName, attribute) {
  const name = attribute.name.toLowerCase();
  const value = attribute.value;

  if (["title", "dir", "lang", "abbr"].includes(name)) return value;

  if (name === "class") {
    const classes = value
      .split(/[\t\n\f\r ]+/)
      .filter(Boolean)
      .filter((token) => SAFE_PREVIEW_CLASSES.some((pattern) => pattern.test(token)));
    return classes.length ? classes.join(" ") : null;
  }

  if (["colspan", "rowspan", "span"].includes(name)) {
    return boundedInteger(value, 100);
  }

  if (["border", "cellpadding", "cellspacing"].includes(name)) {
    return elementName === "table" ? boundedInteger(value, 50) : null;
  }

  if (name === "scope") {
    return ["row", "col", "rowgroup", "colgroup"].includes(value.toLowerCase())
      ? value.toLowerCase()
      : null;
  }

  if (name === "align") {
    return ["left", "right", "center", "justify", "char"].includes(value.toLowerCase())
      ? value.toLowerCase()
      : null;
  }

  if (name === "valign") {
    return ["top", "middle", "bottom", "baseline"].includes(value.toLowerCase())
      ? value.toLowerCase()
      : null;
  }

  return null;
}

function cloneSafePreviewNode(sourceNode, targetDocument) {
  if (sourceNode.nodeType === 3) {
    return targetDocument.createTextNode(sourceNode.nodeValue || "");
  }

  if (sourceNode.nodeType !== 1) return null;

  const elementName = sourceNode.tagName.toLowerCase();
  if (DROP_PREVIEW_CONTENT.has(elementName)) return null;

  if (!SAFE_PREVIEW_ELEMENTS.has(elementName)) {
    const fragment = targetDocument.createDocumentFragment();
    for (const child of sourceNode.childNodes) {
      const safeChild = cloneSafePreviewNode(child, targetDocument);
      if (safeChild) fragment.appendChild(safeChild);
    }
    return fragment;
  }

  const clone = targetDocument.createElement(elementName);
  for (const attribute of sourceNode.attributes) {
    const value = safePreviewAttribute(elementName, attribute);
    if (value !== null) clone.setAttribute(attribute.name.toLowerCase(), value);
  }

  for (const child of sourceNode.childNodes) {
    const safeChild = cloneSafePreviewNode(child, targetDocument);
    if (safeChild) clone.appendChild(safeChild);
  }

  return clone;
}

function sanitisedTablePreview(rawHtml) {
  // DOMParser creates an inert document. Only allowlisted native nodes are
  // rebuilt in the live document, so scripts/custom elements are never moved.
  const parsed = new DOMParser().parseFromString(rawHtml, "text/html");
  const table = parsed.querySelector("table");
  if (!table) return null;

  return cloneSafePreviewNode(table, document);
}

export function createPreservedHtmlRegistry() {
  const randomId = globalThis.crypto?.randomUUID?.();
  const prefix = randomId
    ? `badiu-html-${randomId}`
    : `badiu-html-${Date.now().toString(36)}-${++registrySequence}-${Math.random().toString(36).slice(2)}`;
  const blocks = new Map();
  const idsByHtml = new Map();
  let blockSequence = 0;

  function register(kind, html) {
    const cacheKey = `${kind}\u0000${html}`;
    const existingId = idsByHtml.get(cacheKey);
    if (existingId) return existingId;

    const id = `${prefix}-${kind}-${++blockSequence}`;
    blocks.set(id, { kind, html });
    idsByHtml.set(cacheKey, id);
    return id;
  }

  return {
    protectTables(value) {
      const html = typeof value === "string" ? value : "";

      return replaceTables(html, (tableHtml) => {
        const id = register("table", tableHtml);
        return `<div data-badiu-preserved-html="${id}" data-badiu-preserved-kind="table"></div>`;
      });
    },

    restore(value) {
      const html = typeof value === "string" ? value : "";

      return html.replace(/<div\b([^>]*)>\s*<\/div>/gi, (placeholder, attributes) => {
        const idMatch = attributes.match(
          /\bdata-badiu-preserved-html\s*=\s*(["'])([^"']+)\1/i
        );
        const block = idMatch ? blocks.get(idMatch[2]) : null;
        const kindMatch = attributes.match(
          /\bdata-badiu-preserved-kind\s*=\s*(["'])([^"']+)\1/i
        );
        return block && kindMatch?.[2] === block.kind ? block.html : placeholder;
      });
    },

    get(id) {
      return blocks.get(id) || null;
    },

    get size() {
      return blocks.size;
    }
  };
}

export function createPreservedHtmlExtension(registry, onOpenSource) {
  return Node.create({
    name: "preservedHtmlBlock",
    group: "block",
    atom: true,
    isolating: true,
    selectable: false,
    draggable: false,

    addAttributes() {
      return {
        id: { default: null },
        kind: { default: "table" }
      };
    },

    parseHTML() {
      return [{
        tag: "div[data-badiu-preserved-html]",
        getAttrs: (element) => {
          const id = element.getAttribute("data-badiu-preserved-html");
          const kind = element.getAttribute("data-badiu-preserved-kind") || "table";
          const block = registry.get(id);

          if (!block || block.kind !== kind) return false;
          return { id, kind };
        }
      }];
    },

    renderHTML({ node }) {
      return ["div", {
        "data-badiu-preserved-html": node.attrs.id,
        "data-badiu-preserved-kind": node.attrs.kind
      }];
    },

    addNodeView() {
      return ({ node }) => {
        const dom = document.createElement("div");
        dom.className = "badiu-preserved-html-block";
        dom.contentEditable = "false";
        let editButton = null;

        function render(currentNode) {
          const block = registry.get(currentNode.attrs.id);
          dom.replaceChildren();
          dom.dataset.badiuPreservedHtml = currentNode.attrs.id || "";
          dom.dataset.badiuPreservedKind = currentNode.attrs.kind || "table";
          dom.setAttribute("role", "group");
          dom.setAttribute("aria-label", "Tabela HTML preservada. Edite pelo modo HTML.");
          dom.title = "Tabela HTML preservada — duplo clique para editar o código";

          const controls = document.createElement("div");
          controls.className = "badiu-preserved-html-controls";

          editButton = document.createElement("button");
          editButton.type = "button";
          editButton.className = "badiu-preserved-html-edit";
          editButton.textContent = "Editar tabela no HTML";
          controls.appendChild(editButton);
          dom.appendChild(controls);

          const preview = block?.kind === "table"
            ? sanitisedTablePreview(block.html)
            : null;

          if (preview) {
            dom.appendChild(preview);
          } else {
            const unavailable = document.createElement("p");
            unavailable.className = "badiu-preserved-html-unavailable";
            unavailable.textContent = "Bloco HTML preservado — abra o modo HTML para editar.";
            dom.appendChild(unavailable);
          }
        }

        function isEditButtonEvent(event) {
          return Boolean(
            editButton &&
            (event.target === editButton || editButton.contains(event.target))
          );
        }

        function handleDoubleClick(event) {
          if (isEditButtonEvent(event)) return;
          event.preventDefault();
          onOpenSource?.();
        }

        function handleClick(event) {
          if (!isEditButtonEvent(event)) return;
          event.preventDefault();
          onOpenSource?.();
        }

        render(node);
        dom.addEventListener("dblclick", handleDoubleClick);
        dom.addEventListener("click", handleClick);

        return {
          dom,
          update(updatedNode) {
            if (updatedNode.type.name !== "preservedHtmlBlock") return false;
            render(updatedNode);
            return true;
          },
          ignoreMutation: () => true,
          // The preview is intentionally inert. This prevents typing, Delete or
          // Backspace after a click from replacing the complete atomic table.
          stopEvent: () => true,
          destroy() {
            dom.removeEventListener("dblclick", handleDoubleClick);
            dom.removeEventListener("click", handleClick);
          }
        };
      };
    }
  });
}
